import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSnackbars } from '@customerio/pluma-components/react';
import type { ObjectAttributesEditorState } from '@/features/objects';
import { objectKeys, objectLookupKey, type ObjectRecord } from '@/services/db/object/entity';
import { hasExpectedAttributes, queueObjectUpdate } from '@/services/db/object/mutations';
import { getObject } from '@/services/db/object/queries';
import type { ObjectType } from '@/services/db/object-type/entity';
import { recordSentAttributes, reportObjectSaveError, useObjectSaveExit } from './object-save-flow';

/** The object update is queued; check once after this delay whether it landed. */
const UPDATE_POLL_DELAY_MS = 3000;
const UPDATE_SNACKBAR_ID = 'object.update';

type UseUpdateObjectFlowOptions = {
  objectType: ObjectType;
  workspaceId: number;
  record: ObjectRecord;
  editor: ObjectAttributesEditorState;
  /** Where the save and the discard both land. */
  attributesHref: string;
};

/**
 * The edit-attributes save flow: queue the update, then check once after a short
 * delay whether it landed — clearing the pending note when it did, or leaving a
 * "will be updated soon" one when it did not. Either way the tab is refreshed
 * and the user lands back on it.
 */
export function useUpdateObjectFlow({
  objectType,
  workspaceId,
  record,
  editor,
  attributesHref,
}: UseUpdateObjectFlowOptions) {
  const queryClient = useQueryClient();
  const snackbars = useSnackbars();
  const [isSaving, setIsSaving] = useState(false);
  const exit = useObjectSaveExit(editor);

  /**
   * Seed the freshly-read record under both keys the detail page reads it by, or
   * drop them when the reload failed so the tab re-reads rather than showing a
   * record we know is stale.
   */
  const refreshRecord = (fresh: ObjectRecord | null) => {
    const lookupKey = objectLookupKey(workspaceId, objectType.id, record.id);
    if (fresh == null) {
      void queryClient.invalidateQueries({ queryKey: lookupKey });
      void queryClient.invalidateQueries({ queryKey: objectKeys.detail(workspaceId, record.id) });
      return;
    }
    queryClient.setQueryData(lookupKey, fresh);
    queryClient.setQueryData(objectKeys.detail(workspaceId, fresh.id), fresh);
  };

  const waitForUpdatedObject = async (savedAttributes: Record<string, string>) => {
    snackbars.create({
      variant: 'default',
      id: UPDATE_SNACKBAR_ID,
      message: 'Updating your object…',
      // zero disables the auto-close
      autoCloseTimeout: 0,
    });
    await new Promise((resolve) => setTimeout(resolve, UPDATE_POLL_DELAY_MS));

    // The write was accepted, so a failed reload leaves it unconfirmed rather
    // than rejected — the same outcome as reading back the pre-write values.
    const fresh = await getObject(workspaceId, objectType.id, record.id).catch(() => null);
    const isConfirmed = fresh != null && hasExpectedAttributes(fresh, savedAttributes);

    snackbars.destroy(UPDATE_SNACKBAR_ID);
    if (!isConfirmed) {
      snackbars.create({ variant: 'default', id: UPDATE_SNACKBAR_ID, message: 'Your object will be updated soon.' });
    }
    refreshRecord(fresh);
    exit.resetAndNavigate(attributesHref);
  };

  /** Resolves false when the save was rejected — the error is already reported on the page. */
  const save = async (): Promise<boolean> => {
    const { attrs, hiddenAttrs } = editor.getModelAttributes();
    setIsSaving(true);
    try {
      let savedAttributes;
      try {
        savedAttributes = await queueObjectUpdate({
          workspaceId,
          objectTypeId: objectType.id,
          identifier: record.id,
          attributes: attrs,
          hiddenAttributes: hiddenAttrs,
        });
      } catch (error) {
        reportObjectSaveError({ error, editor, snackbars, verb: 'edit' });
        return false;
      }

      recordSentAttributes(queryClient, workspaceId, objectType.id, savedAttributes);
      await waitForUpdatedObject(savedAttributes);
      return true;
    } finally {
      setIsSaving(false);
    }
  };

  const discard = () => {
    exit.resetAndNavigate(attributesHref);
  };

  return { isSaving, save, discard, hasUnsavedChanges: exit.hasUnsavedChanges };
}
